import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import { StandalonePageShell } from "@/components/site/StandalonePageShell";
import { BRANDS, CATEGORIES } from "@/constants/site";
import { resolveSiteAsset, useSiteContent } from "@/lib/useSiteContent";

function CategoryCard({ category, index, reduceMotion }) {
  return (
    <motion.article
      initial={reduceMotion ? false : { opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: index * 0.07, duration: 0.58, ease: [0.22, 1, 0.36, 1] }}
      className="group relative min-h-[340px] overflow-hidden rounded-[2rem]"
    >
      <img src={resolveSiteAsset(category.image)} alt={category.alt || ""} className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/25 to-ink/5" />
      <div className="absolute inset-x-0 bottom-0 p-7 text-cream sm:p-9">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-cream/65">{String(index + 1).padStart(2, "0")}</span>
        <h3 className="mt-3 font-heading text-3xl font-bold">{category.title}</h3>
        <p className="mt-3 max-w-md text-cream/75">{category.desc}</p>
      </div>
    </motion.article>
  );
}

export default function BrandsPage() {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const home = useSiteContent("home");
  const contact = useSiteContent("contact");
  const dealers = useSiteContent("dealers");
  const brandsContent = useSiteContent("brands");
  const brands = brandsContent.items?.length ? brandsContent.items : BRANDS;
  const categories = home.categories?.length ? home.categories : CATEGORIES;

  return (
    <StandalonePageShell
      content={{ home, contact, dealers }}
      eyebrow={t("pages.brands.eyebrow")}
      title={t("pages.brands.title")}
      description={t("pages.brands.description")}
    >
      <section data-testid="brands-page" className="overflow-x-clip bg-cream px-5 py-20 sm:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl space-y-6 lg:space-y-10">
          {brands.map((brand, index) => (
            <motion.article
              key={brand.name}
              initial={reduceMotion ? false : { opacity: 0, x: index % 2 ? 72 : -72 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.22 }}
              transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
              className={`grid overflow-hidden rounded-[2rem] border border-detail bg-surface lg:min-h-[440px] lg:grid-cols-2 ${index % 2 ? "lg:direction-rtl" : ""}`}
            >
              <div className={`relative min-h-[260px] overflow-hidden bg-white ${index % 2 ? "lg:order-2" : ""}`}>
                <motion.img whileHover={reduceMotion ? undefined : { scale: 1.05 }} transition={{ duration: 0.6 }} src={resolveSiteAsset(brand.image)} alt={brand.alt || `${brand.name} logosu`} className="absolute inset-0 h-full w-full object-cover" />
              </div>
              <div className="flex flex-col justify-center p-7 sm:p-12">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{brand.tag}</span>
                <h2 className="mt-7 font-heading text-4xl font-bold tracking-tight text-ink">{brand.name}</h2>
                <p className="mt-5 max-w-lg leading-relaxed text-ink/65">{brand.text}</p>
                <span className="mt-9 inline-flex items-center gap-2 text-sm font-semibold text-brand">{t("pages.brands.discover")} <ArrowUpRight className="h-4 w-4" /></span>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
      <section id="kategoriler" data-testid="brands-category-section" className="border-t border-detail bg-ink px-5 py-20 text-cream sm:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{t("pages.products.eyebrow")}</span>
          <h2 className="mt-4 max-w-2xl font-heading text-3xl font-bold sm:text-5xl">{home.categories_title || t("pages.products.title")}</h2>
          <p className="mt-5 max-w-2xl leading-relaxed text-cream/70">{t("pages.products.description")}</p>
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {categories.map((category, index) => <CategoryCard key={category.key || category.title} category={category} index={index} reduceMotion={reduceMotion} />)}
          </div>
        </div>
      </section>
    </StandalonePageShell>
  );
}
